import { useEffect, useRef } from "react";
import { calculateDeliveryCost } from "./calculateDelivery";
import { GLOBAL_CONSTANTS } from "../../constants";
import { shouldRoundGazQuantity, shouldUseStrictGazPalletUnits, shouldCountGazByPiece } from "../../utils/gazPalletRules";
import { isGazDeliveryCategoryForRegion, isGazFlowMaterial, isRegionalGikPriceSource, normalizeRegionCode } from "../../utils/gazDeliveryRegion";
import { getComparisonRequestRows } from "./gazComparisonRows";

const PATH_TO_BACKEND = process.env.REACT_APP_PATH_TO_BACKEND;

const REGION_MAP = {
  "Санкт-Петербург": "СПБ",
  "Ленинградская область": "СПБ",
  "Москва": "МСК",
  "Московская область": "МСК",
  "Краснодар": "КРД",
  "Краснодарский край": "КРД",
};

const REGION_COORDS = {
  "СПБ": { lat: 59.9343, lon: 30.3351 },
  "МСК": { lat: 55.7512, lon: 37.6184 },
  "КРД": { lat: 45.0355, lon: 38.9753 },
};

const NUMBER_PER_PALLET_CATEGORIES = new Set([
  "бордюр",
  "бордюр дорожный",
  "плитка тротуарная",
  "газонная решетка",
  "лоток водоотводный жби",
  "лоток водоотводный тротуарный",
]);

const isGazobetonCategory = (value, region) => isGazDeliveryCategoryForRegion(value, region);

const shouldRoundGazProductQuantity = (productData, product = {}, region = null) =>
  !shouldCountGazByPiece(
    productData?.CATEGORY, productData?.CALCULATOR, product?.product_group, product?.category
  ) && (
    isRegionalGikPriceSource(productData?.SOURCE || product?.source, region) ||
    shouldRoundGazQuantity(
      productData?.CATEGORY,
      productData?.CALCULATOR,
      product?.product_group,
      product?.category
    )
  );

const shouldUseStrictGazPalletUnitsForProduct = (productData, product = {}, region = null) =>
  !shouldCountGazByPiece(
    productData?.CATEGORY, productData?.CALCULATOR, product?.product_group, product?.category
  ) && (
    isRegionalGikPriceSource(productData?.SOURCE || product?.source, region) ||
    shouldUseStrictGazPalletUnits(
      productData?.CATEGORY,
      productData?.CALCULATOR,
      product?.product_group,
      product?.category
    )
  );

const isGazDeliveryRow = (row, region) => (
  isGazobetonCategory(row?.CATEGORY, region) ||
  isGazobetonCategory(row?.CALCULATOR, region) ||
  isRegionalGikPriceSource(row?.SOURCE, region)
);

const isGazobetonComparison = (materials = []) =>
  Array.isArray(materials) && materials.some((material) => (
    isGazFlowMaterial(material, material?.region)
  ));

const hasGazobetonDeliveryError = (delivery) => {
  const gazDelivery = delivery?.gaz;
  if (!gazDelivery) return false;

  const details = gazDelivery.details;
  if (!details) return true;
  if (gazDelivery.error === true) return true;

  if (typeof details === "string") {
    return details.toLowerCase().includes("ошибка");
  }

  if (details.error) return true;

  const totalCost = details?.data?.totalCost ?? details?.totalCost;
  return totalCost == null || Number.isNaN(Number(totalCost));
};

const getGazobetonDeliveryErrorMessage = (delivery) => {
  const details = delivery?.gaz?.details;

  if (!details) {
    return "";
  }

  if (typeof details === "string") {
    return details;
  }

  return String(details?.error || "");
};

const isGazManipulatorDeliverySelected = (deliveryMethod) =>
  String(deliveryMethod?.gaz || "")
    .trim()
    .toLowerCase()
    .includes("манипулятор");

const shouldHideGazobetonProduction = (materials, delivery, deliveryMethod) => {
  if (!isGazobetonComparison(materials) || !isGazManipulatorDeliverySelected(deliveryMethod)) {
    return false;
  }

  const errorMessage = getGazobetonDeliveryErrorMessage(delivery);
  return (
    errorMessage.includes("Не заполнена норма погрузки") &&
    errorMessage.includes('"Манипулятор 10тн"')
  );
};

const sourceMatchesRegion = (source, region) => {
  const normalizedRegion = normalizeRegionCode(region);

  if (!normalizedRegion || normalizedRegion === "БЕЛАРУСЬ") {
    return true;
  }

  const normalizedSource = normalizeRegionCode(source);
  return normalizedSource.includes(normalizedRegion);
};

const gazobetonRowMatchesRegion = (productData, region, productionInfo) => {
  const normalizedRegion = normalizeRegionCode(region);
  const normalizedSource = normalizeRegionCode(productData?.SOURCE);
  const rowRegion = normalizeRegionCode(productData?.REGION);
  const productionRegion = normalizeRegionCode(productionInfo?.REGION);

  if (!normalizedRegion || normalizedRegion === "БЕЛАРУСЬ") {
    return true;
  }

  if (rowRegion === normalizedRegion) {
    return true;
  }

  if (normalizedSource === "АКЦИОННЫЕ ТОВАРЫ") {
    return productionRegion === normalizedRegion;
  }

  return sourceMatchesRegion(productData?.SOURCE, region);
};

const parsePositiveNumber = (value) => {
  const parsedValue = Number.parseFloat(String(value ?? "").replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
};

const GAZ_FIELD_ALIASES = {
  PALLET_UNITS: ["PALLET_UNITS", "pallet_per_unit"],
  SHALANDA_PALLETS: ["SHALANDA_PALLETS", "scow_quantitiy"],
  MANIPULATOR_5T: ["MANIPULATOR_5T", "manipulator_5t"],
  MANIPULATOR_10T: ["MANIPULATOR_10T", "manipulator_10t"],
  MANIPULATOR_TRAILER: ["MANIPULATOR_TRAILER", "manipulator_with_trailer"],
  UNITS_PER_PALLET: ["number_per_pallet", "NUMBER_PER_PALLET", "UNITS_PER_PALLET"],
};

const getAliasedPositiveValue = (row, fieldName) => {
  const aliases = GAZ_FIELD_ALIASES[fieldName] || [fieldName];
  for (const alias of aliases) {
    const value = parsePositiveNumber(row?.[alias]);
    if (value !== null) {
      return value;
    }
  }
  return null;
};

const logBackendPalletDebug = (stage, payload) => {
  console.log(`[PALLET_DEBUG][BackendProductions] ${stage}`, payload);
};

const parseFiniteNumber = (value) => {
  const parsedValue = Number.parseFloat(String(value ?? "").replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const isCubicMeterUnit = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/³/g, "3")
    .replace(/\s+/g, "");

  return normalized === "м3" || normalized === "м.куб" || normalized === "мкуб";
};

const getQuantityPerPallet = (product, productData, region = null) => {
  const productQuantityPerPallet = parsePositiveNumber(product.palletUnits);
  const numberPerPallet = getAliasedPositiveValue(productData, "UNITS_PER_PALLET");
  const palletUnits = getAliasedPositiveValue(productData, "PALLET_UNITS");
  const category = String(productData?.CATEGORY || product?.product_group || "").trim().toLowerCase();
  const calculator = String(productData?.CALCULATOR || product?.category || "").trim().toLowerCase();
  const shouldUseOnlyNumberPerPallet = (
    NUMBER_PER_PALLET_CATEGORIES.has(category) ||
    NUMBER_PER_PALLET_CATEGORIES.has(calculator)
  ) && isRegionalGikPriceSource(productData?.SOURCE || product?.source, region);
  const isCubicMeter = isCubicMeterUnit(productData?.BASE_UNIT);
  let resolvedValue = null;
  let resolutionSource = "none";

  if (shouldUseOnlyNumberPerPallet) {
    resolvedValue = numberPerPallet || 1;
    resolutionSource = "strict_number_per_pallet";
  } else if (!isCubicMeter) {
    // product.palletUnits — общее поле (см. комментарий в getGazRoundedPalletMetrics), поэтому
    // его пробуем в последнюю очередь, только если у ЭТОГО производства нет вообще никаких
    // своих норм (ни number_per_pallet, ни PALLET_UNITS).
    resolvedValue = numberPerPallet || palletUnits || productQuantityPerPallet || 1;
    resolutionSource = numberPerPallet
      ? "number_per_pallet"
      : palletUnits
        ? "pallet_units"
        : productQuantityPerPallet
          ? "product.palletUnits"
          : "fallback_1";
  } else {
    resolvedValue = palletUnits || numberPerPallet || productQuantityPerPallet || 1;
    resolutionSource = palletUnits
      ? "pallet_units"
      : numberPerPallet
        ? "number_per_pallet"
        : productQuantityPerPallet
          ? "product.palletUnits"
          : "fallback_1";
  }

  logBackendPalletDebug("getQuantityPerPallet", {
    productName: product?.name || productData?.NOMENCLATURE || "-",
    productionName: productData?.MANUFACTURER || product?.productionName || null,
    source: productData?.SOURCE || product?.source || null,
    category: productData?.CATEGORY || product?.product_group || null,
    calculator: productData?.CALCULATOR || product?.category || null,
    baseUnit: productData?.BASE_UNIT || product?.unit || null,
    raw: {
      number_per_pallet: productData?.number_per_pallet ?? null,
      NUMBER_PER_PALLET: productData?.NUMBER_PER_PALLET ?? null,
      UNITS_PER_PALLET: productData?.UNITS_PER_PALLET ?? null,
      PALLET_UNITS: productData?.PALLET_UNITS ?? null,
      pallet_per_unit: productData?.pallet_per_unit ?? null,
      productPalletUnits: product?.palletUnits ?? null,
    },
    parsed: {
      numberPerPallet,
      palletUnits,
      productQuantityPerPallet,
    },
    shouldUseOnlyNumberPerPallet,
    isCubicMeter,
    resolutionSource,
    resolvedValue,
  });

  return resolvedValue;
};

const getGazRoundedPalletMetrics = (product, productData, region = null) => {
  const requestedQuantity = parseFiniteNumber(product.quantity) || 0;
  const resolvedQuantityPerPallet = getQuantityPerPallet(product, productData, region);
  const shouldUseStrictPalletUnits = shouldUseStrictGazPalletUnitsForProduct(productData, product, region);
  const quantityPerPallet = shouldUseStrictPalletUnits
    ? (getAliasedPositiveValue(productData, "PALLET_UNITS") || resolvedQuantityPerPallet)
    : resolvedQuantityPerPallet;
  const productPallets = parseFiniteNumber(product.numberPallets);
  const shouldTrustProductPallets = Boolean(product._manualNumberPallets || product._manualItemsPerPallet);
  const numberPallets = shouldTrustProductPallets && productPallets !== null && productPallets >= 0
    ? productPallets
    : requestedQuantity > 0
      ? Math.ceil(requestedQuantity / quantityPerPallet)
      : 0;
  // Важно: объём всегда считаем как numberPallets * quantityPerPallet ЭТОГО производства,
  // а не берём product.factVolume напрямую. product.factVolume — общее для всех производств
  // поле (пересчитывается в Товарах при переключении между КП разных производств), поэтому
  // если его использовать здесь, при просмотре КП одного производства объём (и цена) у ВСЕХ
  // остальных производств в сравнении незаметно пересчитывается по чужим нормам поддонов.
  // Количество поддонов (numberPallets) — это осознанный выбор пользователя и его переносить
  // между производствами корректно, а вот объём на поддон у каждого производства свой.
  const factQuantity = numberPallets * quantityPerPallet;

  logBackendPalletDebug("getGazRoundedPalletMetrics", {
    productName: product?.name || productData?.NOMENCLATURE || "-",
    productionName: productData?.MANUFACTURER || product?.productionName || null,
    source: productData?.SOURCE || product?.source || null,
    requestedQuantity,
    rawProductValues: {
      quantity: product?.quantity ?? null,
      numberPallets: product?.numberPallets ?? null,
      factVolume: product?.factVolume ?? null,
      manualNumberPallets: product?._manualNumberPallets ?? null,
      manualItemsPerPallet: product?._manualItemsPerPallet ?? null,
    },
    calculated: {
      resolvedQuantityPerPallet,
      quantityPerPallet,
      numberPallets,
      factQuantity,
      shouldUseStrictPalletUnits,
      shouldTrustProductPallets,
    },
  });

  return {
    requestedQuantity,
    quantityPerPallet,
    numberPallets,
    factQuantity,
  };
};

const normalizeNomenclature = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/х/g, "x")
    .replace(/\s+/g, " ");

const resolveGazCapacity = (productData, productRows, fieldName) => {
  const directValue = getAliasedPositiveValue(productData, fieldName);
  if (directValue !== null) {
    return directValue;
  }

  const guideId = String(productData?.GUIDE_ID || "").trim();
  const normalizedName = normalizeNomenclature(productData?.NOMENCLATURE);
  const fallbackRow = (Array.isArray(productRows) ? productRows : []).find((row) => {
    if (row === productData) {
      return false;
    }

    const rowValue = getAliasedPositiveValue(row, fieldName);
    if (rowValue === null) {
      return false;
    }

    const sameGuideId = guideId && String(row?.GUIDE_ID || "").trim() === guideId;
    const sameName =
      normalizedName &&
      normalizeNomenclature(row?.NOMENCLATURE) === normalizedName;

    return sameGuideId || sameName;
  });

  return fallbackRow ? getAliasedPositiveValue(fallbackRow, fieldName) : null;
};

const resolveGazCapacityDebug = (productData, productRows, fieldName) => {
  const directValue = getAliasedPositiveValue(productData, fieldName);
  if (directValue !== null) {
    return {
      value: directValue,
      source: productData?.SOURCE || "-",
      sourceType: "selected-row",
    };
  }

  const guideId = String(productData?.GUIDE_ID || "").trim();
  const normalizedName = normalizeNomenclature(productData?.NOMENCLATURE);
  const fallbackRow = (Array.isArray(productRows) ? productRows : []).find((row) => {
    if (row === productData) {
      return false;
    }

    const rowValue = getAliasedPositiveValue(row, fieldName);
    if (rowValue === null) {
      return false;
    }

    const sameGuideId = guideId && String(row?.GUIDE_ID || "").trim() === guideId;
    const sameName =
      normalizedName &&
      normalizeNomenclature(row?.NOMENCLATURE) === normalizedName;

    return sameGuideId || sameName;
  });

  return fallbackRow
    ? {
      value: getAliasedPositiveValue(fallbackRow, fieldName),
      source: fallbackRow?.SOURCE || "-",
      sourceType: "fallback-row",
    }
    : {
      value: null,
      source: null,
      sourceType: "not-found",
    };
};

function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

async function determineRegion(addressAndCoordinates) {
  try {
    const { address, coordinates } = addressAndCoordinates || {};
    let regionFullName = null;

    // Геокодирование идёт через наш бэкенд (/api/geocode-region), а не прямо
    // в Nominatim из браузера — у Nominatim жёсткий анти-абьюз лимит, при
    // блокировке/зависании fetch без таймаута навсегда вешал весь расчёт.
    // Таймаут здесь — подстраховка на случай проблем с нашим же бэкендом.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 6000);

    try {
      if (coordinates?.latitude && coordinates?.longitude) {
        const response = await fetch(
          `${PATH_TO_BACKEND}/api/geocode-region?lat=${coordinates.latitude}&lon=${coordinates.longitude}`,
          { signal: controller.signal }
        );
        const data = await response.json();
        regionFullName = data.regionFullName || null;
      } else if (address) {
        const response = await fetch(
          `${PATH_TO_BACKEND}/api/geocode-region?address=${encodeURIComponent(address)}`,
          { signal: controller.signal }
        );
        const data = await response.json();
        regionFullName = data.regionFullName || null;
      }
    } finally {
      clearTimeout(timeoutId);
    }

    if (!regionFullName) {
      console.log("🔹 Регион не определен.");
      return null;
    }

    const region = REGION_MAP[regionFullName];
    if (region) {
      console.log("🔹 Регион найден в списке:", region);
      return region;
    }

    if (coordinates?.latitude && coordinates?.longitude) {
      let closestRegion = null;
      let minDistance = Infinity;
      for (const [reg, coords] of Object.entries(REGION_COORDS)) {
        const distance = calculateDistance(
          coordinates.latitude,
          coordinates.longitude,
          coords.lat,
          coords.lon
        );
        if (distance < minDistance) {
          minDistance = distance;
          closestRegion = reg;
        }
      }
      console.log(`🔹 Неизвестный регион "${regionFullName}", ближайший: ${closestRegion} (${minDistance.toFixed(2)} км)`);
      return closestRegion;
    }

    console.log(`🔹 Неизвестный регион "${regionFullName}", координаты отсутствуют, возвращаем полное название`);
    return regionFullName;
  } catch (error) {
    console.error("Ошибка при определении региона:", error);
    return null;
  }
}

const BackendProductions = ({
  products,
  isMskGazobetonScope = false,
  paymentMethod,
  deliveryMethod,
  addressAndCoordinates,
  selfPickUp,
  setComparisons,
  setIsLoading,
  selectedRegion,
  isNonmetallicMode,
  setAddressValidationError,
  setProductErrors,
  setExcludedByPaymentMethod,
}) => {
  const deliveryCache = useRef(new Map()).current;
  const requestControllerRef = useRef(null);
  const requestSeqRef = useRef(0);

  useEffect(() => {
    // Не выполняем запросы в режиме нерудки - за это отвечает BackendProductionsNonmetallic
    if (isNonmetallicMode) {
      console.log("🔍 BackendProductions: пропускаем выполнение в режиме нерудки");
      // Список отброшенных по форме оплаты остался бы от обычного режима, и на
      // экране нерудки висело бы чужое сообщение.
      setExcludedByPaymentMethod?.([]);
      setIsLoading?.(false);
      return;
    }

    // Игнорируем временные изменения (с флагом _isTemporary)
    const hasTemporaryChanges = products.some(product => product._isTemporary);
    if (hasTemporaryChanges) {
      return;
    }

    console.log("🔍 BackendProductions: products=", products);
    if (requestControllerRef.current) {
      requestControllerRef.current.abort();
    }
    const controller = new AbortController();
    requestControllerRef.current = controller;
    const requestSeq = ++requestSeqRef.current;

    const fetchData = async () => {
      // Внутри МСК Газобетона строка «Товаров» без выбранной номенклатуры — это ещё
      // не позиция заказа: менеджер нажал «Добавить товар» и не успел выбрать товар.
      // Она не участвует в расчёте и не обнуляет сравнение производств.
      // Вне области список берётся как есть — проверка остаётся базовой.
      const rowsForRequest = getComparisonRequestRows({ products, isMskGazobetonScope });

      if (!Array.isArray(rowsForRequest) || !rowsForRequest.length || rowsForRequest.some(p => !p.name)) {
        console.log("🔍 BackendProductions: products невалидны, сбрасываем comparisons");
        setComparisons([]);
        setExcludedByPaymentMethod?.([]);
        setIsLoading?.(false);
        return;
      }

      console.log("🔍 BackendProductions: products валидны, начинаем fetchData");
      setIsLoading?.(true);

      // ----- валидация координат -----
      const { latitude, longitude } = addressAndCoordinates?.coordinates || {};
      const isAddressProvided = addressAndCoordinates?.address && addressAndCoordinates.address.trim() !== '';

      const hasValidCoords = latitude != null && longitude != null && !isNaN(latitude) && !isNaN(longitude);

      if (!hasValidCoords) {
        const errorMessage = isAddressProvided
          ? 'Адрес не распознан. Пожалуйста, укажите координаты вручную.'
          : 'Не удалось определить местоположение по координатам. Проверьте введённые данные.';
        if (setAddressValidationError) setAddressValidationError(errorMessage);
        setIsLoading?.(false);
        return;
      } else {
        // Если координаты валидны, сбрасываем предыдущую ошибку
        if (setAddressValidationError) setAddressValidationError(null);
      }
      // ----- валидация координат -----

      try {
        const detectedRegion = selfPickUp ? selectedRegion : await determineRegion(addressAndCoordinates);
        const region = detectedRegion || selectedRegion || addressAndCoordinates?.region || null;
        console.log("🔹 Используемый регион:", region);

        // Создаем копию products, заменяя null на 100 для расчетов
        const productsForCalc = rowsForRequest.map(product => ({
          ...product,
          quantity: product.quantity == null || product.quantity === 0 ? 1 : product.quantity,
        }));

        const response = await fetch(`${PATH_TO_BACKEND}/api/filter`, {
          signal: controller.signal,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            names: productsForCalc.map(p => p.name).filter(Boolean),
            requestedProducts: productsForCalc.map((product) => ({
              name: product.name,
              category: product.product_group || null,
              source: product.source || null,
              width: product.width ?? null,
              height: product.height ?? null,
              density: product.density ?? null,
            })),
            region,
            paymentMethod,
            // Исключаем нерудные материалы из обычного компонента
            excludeNonmetallic: true,
          }),
        });

        if (!response.ok) throw new Error(`Ошибка: ${response.status}`);
        const { data, excludedByPaymentMethod } = await response.json();
        if (controller.signal.aborted || requestSeq !== requestSeqRef.current) {
          return;
        }

        // Производства, отброшенные сервером по форме оплаты. Выставляется до
        // проверки validManufacturers: случай «отбросили всех, сравнивать нечего» —
        // именно тот, ради которого признак и нужен.
        setExcludedByPaymentMethod?.(
          Array.isArray(excludedByPaymentMethod) ? excludedByPaymentMethod : []
        );

        const validManufacturers = Object.entries(data || {})
          .filter(([, { products }]) =>
            products && Object.values(products).some(rows => rows?.length > 0)
          );

        if (!validManufacturers.length) {
          console.log("🔍 BackendProductions: validManufacturers пуст, сбрасываем comparisons");
          setComparisons([]);
          setIsLoading?.(false);
          return;
        }

        const newComparisons = await Promise.all(
          validManufacturers.map(async ([manufacturer, { products: manufacturerProducts, productionInfo, source: apiResolvedSource }]) => {
            const deliveryManufacturer = productionInfo?.NAME || manufacturer;
            const displayManufacturer = productionInfo?.NAME || manufacturer;
            const createMappedMaterial = (product, productData, productRows = []) => {
              const isGazobeton = isGazDeliveryRow(productData, region);
              const shouldRoundQuantity = shouldRoundGazProductQuantity(productData, product, region);
              const requestedQuantity = Number(product.quantity) || 0;
              const gazMetrics = shouldRoundQuantity ? getGazRoundedPalletMetrics(product, productData, region) : null;
              const palletUnits = gazMetrics?.quantityPerPallet || getQuantityPerPallet(product, productData, region) || 1;
              const numberPallets = gazMetrics?.numberPallets ?? (requestedQuantity > 0 ? Math.ceil(requestedQuantity / palletUnits) : 0);
              const factQuantity = shouldRoundQuantity ? gazMetrics.factQuantity : requestedQuantity;

              if (isGazobeton) {
                logBackendPalletDebug("createMappedMaterial", {
                  productName: product?.name || productData?.NOMENCLATURE || "-",
                  manufacturer: productData?.MANUFACTURER || null,
                  source: productData?.SOURCE || null,
                  shouldRoundQuantity,
                  requestedQuantity,
                  raw: {
                    number_per_pallet: productData?.number_per_pallet ?? null,
                    NUMBER_PER_PALLET: productData?.NUMBER_PER_PALLET ?? null,
                    UNITS_PER_PALLET: productData?.UNITS_PER_PALLET ?? null,
                    PALLET_UNITS: productData?.PALLET_UNITS ?? null,
                    pallet_per_unit: productData?.pallet_per_unit ?? null,
                  },
                  gazMetrics,
                  output: {
                    palletUnits,
                    numberPallets,
                    factQuantity,
                  },
                });
              }

              const shalandaCapacity = isGazobeton
                ? resolveGazCapacity(productData, productRows, "SHALANDA_PALLETS")
                : null;
              const manipulatorCapacity = isGazobeton
                ? resolveGazCapacity(productData, productRows, "MANIPULATOR_10T")
                : null;
              const manipulator5tCapacity = isGazobeton
                ? resolveGazCapacity(productData, productRows, "MANIPULATOR_5T")
                : null;
              const totalPallets = shalandaCapacity || 0;
              const freePallets = isGazobeton && totalPallets > 0
                ? Math.max(totalPallets - numberPallets, 0)
                : 0;

              return {
                totalPallets: isGazobeton && totalPallets > 0 ? totalPallets : "-",
                occupiedPallets: isGazobeton ? numberPallets : "-",
                freePallets: isGazobeton && totalPallets > 0 ? freePallets : "-",
                calculator: productData.CALCULATOR || "Нет данных",
                category: productData.CATEGORY || "Нет данных",
                name: productData.NOMENCLATURE || product.name,
                productId: product.id,
                requestedName: product.name,
                quantity: factQuantity,
                factVolume: isGazobeton ? factQuantity : "-",
                unit: productData.BASE_UNIT || "шт",
                pricePerOne: Number((paymentMethod === "cash"
                  ? productData.PRICE_CASH
                  : productData.PRICE_NONCASH)?.toString().replace(/\s/g, "")) || 0,
                date: productData.PRICE_UPDATE_DATE || "Нет данных",
                description: productData.DESCRIPTION || "Нет данных",
                massPerOne: Number(productData.WEIGHT_TONS?.toString().replace(",", ".")) || 0,
                volumePerOne: Number(productData.VOLUME_CUBIC?.toString().replace(",", ".")) || 1,
                isOldJbi: productData.IS_OLD_JBI || false,
                manufacturer: displayManufacturer,
                numberPallets: isGazobeton
                  ? numberPallets
                  : Math.ceil(requestedQuantity / palletUnits),
                palletUnits,
                shalandaCapacity,
                manipulator5tCapacity,
                manipulatorCapacity,
                bitrixId: productData.BITRIX_ID || "",
                source: apiResolvedSource || productData.SOURCE || "-",
                guideId: productData.GUIDE_ID || null,
                unitsPerPallet: getAliasedPositiveValue(productData, "UNITS_PER_PALLET") || 0,
              };
            };

            const createDeliveryMaterial = (product, productData, productRows = []) => {
              const isGazobeton = isGazDeliveryRow(productData, region);
              const shouldRoundQuantity = shouldRoundGazProductQuantity(productData, product, region);
              const requestedQuantity = Number(product.quantity) || 0;
              const gazMetrics = shouldRoundQuantity ? getGazRoundedPalletMetrics(product, productData, region) : null;
              const palletUnits = gazMetrics?.quantityPerPallet || getQuantityPerPallet(product, productData, region) || 1;
              const numberPallets = gazMetrics?.numberPallets ?? (requestedQuantity > 0 ? Math.ceil(requestedQuantity / palletUnits) : 0);
              const factQuantity = shouldRoundQuantity ? gazMetrics.factQuantity : requestedQuantity;

              if (isGazobeton) {
                logBackendPalletDebug("createDeliveryMaterial", {
                  productName: product?.name || productData?.NOMENCLATURE || "-",
                  manufacturer: productData?.MANUFACTURER || null,
                  source: productData?.SOURCE || null,
                  shouldRoundQuantity,
                  requestedQuantity,
                  raw: {
                    number_per_pallet: productData?.number_per_pallet ?? null,
                    NUMBER_PER_PALLET: productData?.NUMBER_PER_PALLET ?? null,
                    UNITS_PER_PALLET: productData?.UNITS_PER_PALLET ?? null,
                    PALLET_UNITS: productData?.PALLET_UNITS ?? null,
                    pallet_per_unit: productData?.pallet_per_unit ?? null,
                  },
                  gazMetrics,
                  output: {
                    palletUnits,
                    numberPallets,
                    factQuantity,
                  },
                });
              }

              const shalandaCapacityDebug = isGazobeton
                ? resolveGazCapacityDebug(productData, productRows, "SHALANDA_PALLETS")
                : { value: null, source: null, sourceType: null };
              const manipulatorCapacityDebug = isGazobeton
                ? resolveGazCapacityDebug(productData, productRows, "MANIPULATOR_10T")
                : { value: null, source: null, sourceType: null };
              const manipulator5tCapacityDebug = isGazobeton
                ? resolveGazCapacityDebug(productData, productRows, "MANIPULATOR_5T")
                : { value: null, source: null, sourceType: null };
              const shalandaCapacity = shalandaCapacityDebug.value;
              const manipulatorCapacity = manipulatorCapacityDebug.value;
              const manipulator5tCapacity = manipulator5tCapacityDebug.value;

              if (isGazobeton) {
                console.log("🧱 [gaz delivery source]", {
                  region,
                  manufacturer,
                  productionRegion: productionInfo?.REGION || null,
                  name: productData.NOMENCLATURE || product.name,
                  selectedSource: productData.SOURCE || "-",
                  rowRegion: productData.REGION || null,
                  guideId: productData.GUIDE_ID || null,
                  shalandaCapacity,
                  shalandaCapacitySource: shalandaCapacityDebug.source,
                  shalandaCapacitySourceType: shalandaCapacityDebug.sourceType,
                  manipulatorCapacity,
                  manipulator5tCapacity,
                  manipulatorCapacitySource: manipulatorCapacityDebug.source,
                  manipulatorCapacitySourceType: manipulatorCapacityDebug.sourceType,
                  manipulator5tCapacitySource: manipulator5tCapacityDebug.source,
                  manipulator5tCapacitySourceType: manipulator5tCapacityDebug.sourceType,
                  candidateSources: productRows.map((row) => ({
                    name: row?.NOMENCLATURE || null,
                    source: row?.SOURCE || "-",
                    region: row?.REGION || null,
                    rawPalletUnits: row?.PALLET_UNITS ?? row?.pallet_per_unit ?? null,
                    rawShalanda: row?.SHALANDA_PALLETS ?? row?.scow_quantitiy ?? null,
                    rawManipulator5t: row?.MANIPULATOR_5T ?? row?.manipulator_5t ?? null,
                    rawManipulator: row?.MANIPULATOR_10T ?? row?.manipulator_10t ?? null,
                    shalanda: getAliasedPositiveValue(row, "SHALANDA_PALLETS"),
                    manipulator5t: getAliasedPositiveValue(row, "MANIPULATOR_5T"),
                    manipulator: getAliasedPositiveValue(row, "MANIPULATOR_10T"),
                  })),
                });
              }

              return {
                calculator: productData.CALCULATOR || "Нет данных",
                category: productData.CATEGORY || "Нет данных",
                name: productData.NOMENCLATURE || product.name,
                productId: product.id,
                requestedName: product.name,
                quantity: factQuantity,
                factVolume: isGazobeton ? factQuantity : "-",
                unit: productData.BASE_UNIT || "шт",
                pricePerOne: Number((paymentMethod === "cash"
                  ? productData.PRICE_CASH
                  : productData.PRICE_NONCASH)?.toString().replace(/\s/g, "")) || 0,
                date: productData.PRICE_UPDATE_DATE || "Нет данных",
                description: productData.DESCRIPTION || "Нет данных",
                massPerOne: Number(productData.WEIGHT_TONS?.toString().replace(",", ".")) || 0,
                volumePerOne: Number(productData.VOLUME_CUBIC?.toString().replace(",", ".")) || 1,
                isOldJbi: productData.IS_OLD_JBI || false,
                manufacturer: deliveryManufacturer,
                numberPallets: numberPallets,
                occupiedPallets: isGazobeton ? numberPallets : "-",
                palletUnits,
                shalandaCapacity,
                manipulator5tCapacity,
                manipulatorCapacity,
                source: productData.SOURCE || "-",
                region: productData.REGION || null,
                capacitySource: {
                  shalanda: shalandaCapacityDebug,
                  manipulator5t: manipulator5tCapacityDebug,
                  manipulator: manipulatorCapacityDebug,
                },
                bitrixId: productData.BITRIX_ID || "",
                guideId: productData.GUIDE_ID || null,
              };
            };

            const displayMaterials = productsForCalc.flatMap(product => {
              const rawProductRows = manufacturerProducts[product.name] || [];
              const productRows = rawProductRows.filter((productData) => {
                if (!isGazDeliveryRow(productData, region)) {
                  return true;
                }

                const matches = gazobetonRowMatchesRegion(productData, region, productionInfo);
                if (!matches) {
                  console.log(
                    `⏭️ Исключаем газобетонную позицию по SOURCE: ${manufacturer} / ${product.name} / ${productData.SOURCE} для региона ${region}`
                  );
                }
                return matches;
              });
              console.log(`🔍 ${product.name}: ${productRows.length} записей из ${manufacturer}`);
              if (!productRows.length) return [];

              const isJbiRow = productRows[0]?.CALCULATOR === "ЖБИ";
              if (!isJbiRow) {
                return [createMappedMaterial(product, productRows[0], productRows)];
              }

              // Для ЖБИ у одной номенклатуры может быть несколько строк прайса
              // с разным "Описание" (например, ГОСТ и ТУ) — показываем каждую
              // отдельной строкой сравнения, а не только первую попавшуюся.
              const seenDescriptions = new Set();
              const uniqueRows = productRows.filter((row) => {
                const description = row.DESCRIPTION || "Нет данных";
                if (seenDescriptions.has(description)) return false;
                seenDescriptions.add(description);
                return true;
              });

              return uniqueRows.map((row) => createMappedMaterial(product, row, productRows));
            }).filter(Boolean);

            const deliveryMaterials = productsForCalc.map(product => {
              const productRows = (manufacturerProducts[product.name] || []).filter((productData) => {
                if (!isGazDeliveryRow(productData, region)) {
                  return true;
                }

                return gazobetonRowMatchesRegion(productData, region, productionInfo);
              });
              if (!productRows.length) return null;

              const firstRow = productRows[0];
              return createDeliveryMaterial(product, firstRow, productRows);
            }).filter(Boolean);

            console.log(`🚚 deliveryMaterials для ${manufacturer}:`, deliveryMaterials);

            if (!displayMaterials.length || !deliveryMaterials.length) {
              console.log(`⏭️ Пропускаем ${manufacturer}: после фильтрации по SOURCE не осталось подходящих материалов для региона ${region}`);
              return null;
            }

            const cacheKey = JSON.stringify({
              materials: deliveryMaterials,
              deliveryMethod,
              productionInfo,
              selfPickUp,
              addressAndCoordinates,
              paymentMethod,
            });

            if (deliveryCache.has(cacheKey)) {
              const cachedDelivery = deliveryCache.get(cacheKey);
              if (shouldHideGazobetonProduction(displayMaterials, cachedDelivery, deliveryMethod)) {
                console.log(`⏭️ ${manufacturer}: скрываем производство из-за отсутствующей нормы погрузки манипулятора`);
                return null;
              }

              return {
                id: manufacturer,
                production: displayManufacturer,
                materials: displayMaterials,
                productionInfo,
                delivery: cachedDelivery,
                region,
              };
            }

            const delivery = selfPickUp
              ? Object.fromEntries(
                ["os", "jbi", "gaz", "ner", "mix", "pilmat", "grandline", "metall"].map(cat => [
                  cat,
                  {
                    category: GLOBAL_CONSTANTS.CATEGORY_NAMES[cat] || "Прочее",
                    details: { data: { totalCost: 0, numberOfTrips: 0, costPerTrip: 0, bestMethod: "Самовывоз" } },
                  },
                ])
              )
              : await calculateDeliveryCost(
                deliveryMaterials,
                deliveryMethod,
                productionInfo,
                selfPickUp,
                addressAndCoordinates,
                region,
                paymentMethod,
                false,
                controller.signal,
              );

            // Проверяем наличие ошибки в доставке
            let hasDeliveryError = false;
            let errorMessage = '';

            if (delivery && typeof delivery === 'object') {
              for (const key of Object.keys(delivery)) {
                const category = delivery[key];
                if (category && category.error === true) {
                  hasDeliveryError = true;
                  errorMessage = category.details || 'Ошибка при расчёте доставки';
                  break;
                }
              }
            }

            if (hasDeliveryError && displayMaterials.length && setProductErrors) {
              const errors = {};
              displayMaterials.forEach(mat => {
                const product = products.find(p => p.id === mat.productId);
                if (product) {
                  errors[product.id] = errorMessage;
                }
              });
              setProductErrors(prev => ({ ...prev, ...errors }));
            }

            if (controller.signal.aborted || requestSeq !== requestSeqRef.current) {
              return null;
            }

            if (paymentMethod === "card" && delivery["os"]) {
              const osDelivery = delivery["os"];
              if (osDelivery.details && osDelivery.details.data) {
                const originalCost = osDelivery.details.data.costPerTrip || 0;
                osDelivery.details.data.costPerTrip = originalCost * 1.1;
                osDelivery.details.data.totalCost = osDelivery.details.data.costPerTrip * osDelivery.details.data.numberOfTrips;
                console.log(`🔹 Увеличена стоимость доставки для категории "os" на 10%: ${originalCost} → ${osDelivery.details.data.costPerTrip}`);
              }
            }

            deliveryCache.set(cacheKey, delivery);
            if (shouldHideGazobetonProduction(displayMaterials, delivery, deliveryMethod)) {
              console.log(`⏭️ ${manufacturer}: скрываем производство из-за отсутствующей нормы погрузки манипулятора`);
              return null;
            }

            if (isGazobetonComparison(displayMaterials) && hasGazobetonDeliveryError(delivery)) {
              console.log(`⚠️ ${manufacturer}: ошибка доставки для газобетона, оставляем строку для отображения ошибки`);
            }

            return {
              id: manufacturer,
              production: displayManufacturer,
              materials: displayMaterials,
              productionInfo,
              delivery,
              region,
            };
          })
        );

        const filteredComparisons = newComparisons.filter(Boolean);
        console.log("🔍 BackendProductions: newComparisons=", filteredComparisons);
        if (!controller.signal.aborted && requestSeq === requestSeqRef.current) {
          setComparisons(filteredComparisons);
        }
      } catch (err) {
        if (err?.name === "AbortError") {
          return;
        }
        console.error("❌ Ошибка:", err);
        if (requestSeq === requestSeqRef.current) {
          setComparisons([]);
          setExcludedByPaymentMethod?.([]);
        }
      } finally {
        if (requestSeq === requestSeqRef.current) {
          setIsLoading?.(false);
        }
      }
    };

    fetchData();

    return () => {
      controller.abort();
    };
  }, [products, isMskGazobetonScope, paymentMethod, deliveryMethod, addressAndCoordinates, selfPickUp, setComparisons, setIsLoading, setExcludedByPaymentMethod, deliveryCache, selectedRegion, isNonmetallicMode]);

  return null;
};

export { shouldHideGazobetonProduction };

export default BackendProductions;
